import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { INTENT_TYPES, PROPERTY_TYPES } from '../../services/branding';
import Editor from '../editor/editor';
import { startCase } from 'lodash-es';

import { Form, Tag, Row, Col, Spin } from 'antd';
import Preview from '../preview/preview';

import { getKey, getName, getFallbackOptions, getMostSpecificId } from '../../utils/hierarchy';
import BrandingService from '../../services/branding';
import ImageService from '../../services/image';

import './element.less';

export default class Element extends React.Component {
    state = {
        loading: false,
        elementProperties: [],
        styles: []
    };

    static propTypes = {
        auth: PropTypes.shape( {
            state: PropTypes.shape( {
                access_token: PropTypes.string.isRequired,
                claims: PropTypes.shape ( {
                    company_id: PropTypes.string.isRequired
                } )
            } )
        } ),
        solutionId: PropTypes.string,
        experienceId: PropTypes.string,
        programId: PropTypes.string,
        intent: PropTypes.string,
        preview: PropTypes.bool
    };

    static getDerivedStateFromProps( props, state ) {
        if ( state.hierarchyKey !== getKey( props ) ) {
            return {
                hierarchyKey: null
            };
        }
        return null;
    }

    componentDidMount() {
        this.load();
    }
    componentDidUpdate( prevProps, prevState ) {
        const { loading, hierarchyKey } = this.state;

        if ( !hierarchyKey && !loading ) {
            this.load();
        }
    }

    render() {
        const {
            loading,
            elementProperties
        } = this.state;

        const {
            auth,
            themeId,
            solutionId,
            experienceId,
            programId,
            elementName,
            intent = INTENT_TYPES.NONE,
            preview,
            readOnly,
            disabled,
            hideFallback
        } = this.props;
        return (
            loading ? (
                <Spin />
            ) : (
                <Form
                    className="branding-element"
                    name="overview"
                    layout="vertical"
                >
                    <Row>
                        <Col span={ preview ? 8 : 24 }>
                            {
                                elementProperties.map( elementProperty => {
                                    const style = this.getStyle( elementProperty, intent );

                                    return (
                                        <Form.Item
                                            key={ elementProperty.id }
                                            name={ elementProperty.id }
                                            className="element-property-item"
                                            label={ (
                                                <Fragment>
                                                    {  this.renderPropertyLabel( elementProperty ) }
                                                    {
                                                        style && !style.allowClear && style.fallbackFrom && !hideFallback && (
                                                            <div className="fallback-tag-container">
                                                                <Tag
                                                                    color="blue"
                                                                    className="fallback-tag"
                                                                >
                                                                    { getName( style.fallbackFrom ) }
                                                                </Tag>
                                                            </div>
                                                        )
                                                    }
                                                </Fragment>
                                            ) }
                                        >
                                            <Editor
                                                style={ style ? !style.value && style.fallbackStyle ? style.fallbackStyle : style : null }
                                                type={ elementProperty.propertyType }
                                                auth={ auth }
                                                themeId= { themeId }
                                                solutionId={ solutionId }
                                                experienceId={ experienceId }
                                                programId={ programId }
                                                onChange={ this.handleElementPropertyChange( style ) }
                                                allowClear={ style && style.allowClear }
                                                readOnly={ readOnly }
                                                disabled={ disabled }
                                            />
                                        </Form.Item>
                                    );
                                } )
                            }
                        </Col>
                        {
                            preview && (
                                <Col span={ 16 }>
                                    <div className="preview-container">
                                        <Preview
                                            elementName={ elementName }
                                            readOnly={ readOnly }
                                            disabled={ disabled }
                                            styles={ this.state.styles.filter( s => s.intent === intent ) }
                                        />
                                    </div>
                                </Col>
                            )
                        }
                    </Row>
                </Form>
            )
        );
    }
    renderPropertyLabel = ( elementProperty ) => {
        const { onRenderLabel } = this.props;
        const label =  startCase( elementProperty.propertyName.toLowerCase() ) ;

        if ( typeof onRenderLabel === 'function' ) {
            return onRenderLabel( { label, elementProperty } );
        }

        return label;
    };

    handleElementPropertyChange = ( style ) => ( value ) => {
        this.setState( {
            styles: this.state.styles.map( s => {
                // if it's not the style being changed, just return what it was
                if ( s.id !== style.id ) {
                    return s;
                }

                return {
                    ...s,
                    isChanged: true,
                    allowClear: !!value,
                    fallbackFrom: (
                        s.fallbackStyle ? s.fallbackStyle.fallbackFrom : ''
                    ),
                    value,
                    originalValue: (
                        s.originalValue === undefined
                            ? s.value
                            : s.originalValue
                    )
                };
            } )
        } );
    };

    getStyle = ( elementProperty, intent ) => {
        const { styles } = this.state;

        return styles.find( style => (
            style.elementPropertyId === elementProperty.id &&
            ( style.intent || INTENT_TYPES.NONE ) === ( intent || INTENT_TYPES.NONE )
        ) );
    };

    load = async () => {
        const { auth, themeId, solutionId, experienceId, programId, elementName, intent, propertyNames, onLoaded, hideFallback } = this.props;
        const options = { themeId, solutionId, experienceId, programId, elementName };
        const fallbackOptions = { ...getFallbackOptions( options ), elementName };

        this.setState( {
            loading: true,
            loadingErrors: null,
            hierarchyKey: getKey( this.props )
        } );

        try {
            const brandingService = new BrandingService( auth.state );

            let { elementProperties } = await brandingService.getElementProperties();
            elementProperties = elementProperties.filter( ep => ep.elementName === elementName );
            let { styles } = await brandingService.getStyles( options );
            let { styles: fallbackStyles } = await brandingService.getStyles( fallbackOptions );

            if ( hideFallback ) {
                fallbackStyles = [];

                styles.forEach( style => {
                    if ( style.fallbackFrom ) {
                        style.fallbackStyle = null;
                        style.value = null;
                    }
                } );
            }

            // get what intents the elements have; TODO: add this to the elements table so it's more exact
            const intents = [];
            intents.push( intent );
            styles.forEach( style => {
                if ( !intents.includes( style.intent ) ) {
                    intents.push( style.intent );
                }
            } );
            // go through the fallback styles and add the fallbackFrom
            fallbackStyles.forEach( fallbackStyle => {
                fallbackStyle.fallbackFrom = getMostSpecificId( auth.state.claims.company_id, fallbackStyle );
            } );
            // go through the styles and make copies or add them if they don't exist
            elementProperties = elementProperties.filter( ep => (
                ep.elementName === elementName &&
                ( !Array.isArray( propertyNames ) || propertyNames.includes( ep.propertyName ) )
            ) )
            .sort( ( a, b ) => a.propertyName.localeCompare( b.propertyName ) );
            // go through the styles and adjust obased on the element properties
            styles = elementProperties.map( elementProperty => {
                // try to find the style
                let style = styles.find( style => (
                    style.elementPropertyId === elementProperty.id &&
                    ( style.intent || INTENT_TYPES.NONE ) === ( intent || INTENT_TYPES.NONE )
                ) );
                // make sure if the requested themeId is null if the themeId on the style isn't undefined
                if ( style && style.themeId === null && !style.themeId ) {
                    style.themeId = null;
                }
                // if the style is from a fallback, we need to make a copy
                if ( style && style.fallbackFrom ) {
                    style.isCopy = true;
                    style.isChanged = false;
                    style.id = uuid();
                    style.themeId = themeId;
                    style.solutionId = solutionId;
                    style.experienceId = experienceId;
                    style.programId = programId;
                }
                // the style doesn't exist, we need to create it
                if ( !style ) {
                    style = {
                        isNew: true,
                        isCopy: false,
                        isChanged: false,
                        id: uuid(),
                        companyId: auth.state.claims.company_id,
                        elementName: elementProperty.elementName,
                        elementPropertyId: elementProperty.id,
                        propertyName: elementProperty.propertyName,
                        propertyType: elementProperty.propertyType,
                        intent,
                        themeId,
                        experienceId,
                        solutionId,
                        programId,
                        value: null
                    };
                    styles.push( style );
                }
                // set a fallback style, in case it's cleared
                style.fallbackStyle = fallbackStyles.find( fallbackStyle => (
                    fallbackStyle.elementPropertyId === elementProperty.id &&
                    ( fallbackStyle.intent || INTENT_TYPES.NONE ) === ( intent || INTENT_TYPES.NONE ) &&
                    fallbackStyle.id !== style.id
                ) );
                style.allowClear = !style.fallbackFrom && !!style.fallbackStyle;

                return style;
            } );
            // filter out any styles where the properties aren't being edited
            styles = styles.filter( style => {
                if ( !Array.isArray( propertyNames ) || propertyNames.length === 0 ) {
                    return true;
                }
                return propertyNames.indexOf( style.propertyName ) >= 0;
            } );
            this.setState( {
                loading: false,
                elementProperties,
                intents,
                styles
            } );

            if ( typeof onLoaded === 'function' ) {
                onLoaded( {
                    elementProperties,
                    intents,
                    styles
                } );
            }

         } catch ( { errors } ) {
            this.setState( {
                loading: false,
                loadingErrors: errors
            } );
        }
    };
    save = async () => {
        const { auth } = this.props;

        const stylesToSave = ( this.state.styles
            // only change styles that have changed.
            .filter( style => {
                return ( style.isChanged && style.originalValue !== style.value );
            } )
        );

        if ( stylesToSave.length === 0 ) {
            return;
        }

        this.setState( {
            saving: true
        } );

        try {
            // images need to be saved in the studio, so go through the styles and save any images (url)
            const imageService = new ImageService( auth.state );
            for ( let i = 0; i < stylesToSave.length; i++ ) {
                if ( stylesToSave[ i ].propertyType === PROPERTY_TYPES.URL && stylesToSave[ i ].value ) {
                    const { proxyUrl } = await imageService.saveImage( stylesToSave[ i ].value );
                    stylesToSave[ i ].value = proxyUrl;
                }
            }

            const brandingService = new BrandingService( auth.state );
            // save the colors, fallback colors need a new id
            await brandingService.upsertStyles( stylesToSave );
            // reuse the load function, it does a lot of heavy lifting with the fallback logic, no reason to redo all of that here.
            await this.load();

            this.setState( {
                saving: false
            } );
         } catch ( ex ) {
            this.setState( {
                saving: false
            } );
            throw ex;
        }
    };
}
